import {
  Event,
  History,
  Hotel,
  Info,
  People,
  Person,
  Phone,
  Wc
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import { fetchPatient } from "../../redux/patient/patientAction";

// Define styled components with framer-motion
const MotionCard = motion(Card);
const MotionPaper = motion(Paper);
const MotionTableRow = motion(TableRow);
const MotionAccordion = motion(Accordion);

const ViewPatient = () => {
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const patientState = useSelector(
    (state) => state?.patients?.fetchPatient || {}
  );
  const patientData = patientState?.data?.data || null;

  const { id } = useParams();

  useEffect(() => {
    dispatch(fetchPatient(id));
  }, [dispatch, id]);

  const patient = patientData
    ? {
        name: patientData.patient_name || "N/A",
        wardNumber: patientData.Ward?.ward_name || "N/A",
        bedNumber: patientData.ip_number || "N/A",
        admissionDate: patientData.createdAt,
        dischargeDate: null,
        age: patientData.User?.age || "N/A",
        gender:
          patientData.gender_type === "M"
            ? "Male"
            : patientData.gender_type === "F"
            ? "Female"
            : "Other",
        department: patientData.Department?.department_name || "N/A",
        block: patientData.Block?.block_name || "N/A",
        mobileNumber: patientData.phone || "N/A",
        status:
          patientData.User?.status === 1
            ? "Active"
            : patientData.User?.status === 2
            ? "Inactive"
            : "N/A",
        block_id: patientData.block_id || "N/A",
        ward_id: patientData.ward_id || "N/A",
        floor_id: patientData.floor_id || "N/A",
      }
    : {
        name: "Loading...",
        wardNumber: "Loading...",
        bedNumber: "Loading...",
        admissionDate: null,
        dischargeDate: null,
        age: "Loading...",
        gender: "Loading...",
        department: "Loading...",
        block: "Loading...",
        mobileNumber: "Loading...",
        status: "Loading...",
        block_id: "Loading...",
        ward_id: "Loading...",
        floor_id: "Loading...",
      };


  const attenders = patientData?.Attenders || [];
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    if (typeof status === "number") {
      return status === 1 ? "success" : "error";
    }

    switch (status?.toLowerCase()) {
      case "admitted":
        return "primary";
      case "discharged":
        return "success";
      case "critical":
        return "error";
      case "stable":
        return "info";
      case "active":
        return "success";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  const getAttenderStatusLabel = (status) => {
    return status === 1 ? "Checked Out" : status === 2 ? "Checked In" : "Unknown";
  };

  const getAttenderStatusColor = (status) => {
    return status === 1 ? "success" : status === 2 ? "primary" : "default";
  };

  const getAttenderTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "spouse":
      case "spouce":
        return "#8E24AA";
      case "parent":
        return "#1E88E5";
      case "child":
        return "#43A047";
      case "friend":
        return "#FF9800"; 
      case "sibling":
        return "#d32f2f";
      default:
        return "#5E35B1"; 
    }
  };

  const getAttenderTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "spouce":
      case "spouse":
        return "💑";
      case "parent":
        return "👪";
      case "child":
        return "👶";
      case "friend":
        return "🤝";
      case "sibling":
        return "👫";
      default:
        return "👤";
    }
  };

  const handleBack = () => {
    Navigate(APP_LINK.LISTALLPATIENT);
  };


  const totalVisits = attenders.reduce((total, attender) => {
    return total + (attender.attenderHistories?.length || 0);
  }, 0);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: i => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    })
  };

  const accordionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 80,
        damping: 15
      }
    })
  };

  return (
    <Box sx={{ maxWidth: "100vw", mx: "auto", p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8} lg={9} xl={11}>
          <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
            Patient Details
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={3} xl={1} mb={2}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </motion.div>
        </Grid>
      </Grid>

      <MotionCard 
        elevation={3} 
        sx={{ mb: 4 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5" component="div">
              <Person sx={{ verticalAlign: "middle", mr: 1 }} />
              Patient Information
            </Typography>
            <Chip
              label={patient.status}
              color={getStatusColor(patient.status)}
              icon={<Info />}
              variant="outlined"
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <Person
                  sx={{ fontSize: "small", verticalAlign: "middle", mr: 1 }}
                />
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.name}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <Wc
                  sx={{ fontSize: "small", verticalAlign: "middle", mr: 1 }}
                />
                Gender
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.gender}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <Hotel
                  sx={{ fontSize: "small", verticalAlign: "middle", mr: 1 }}
                />
                 Serial No
              </Typography>
              <Typography variant="body1" gutterBottom>
               {patient.bedNumber}
              </Typography>
            </Grid>


            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <Event
                  sx={{ fontSize: "small", verticalAlign: "middle", mr: 1 }}
                />
                Admission Date
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatDate(patient.admissionDate)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <Phone
                  sx={{ fontSize: "small", verticalAlign: "middle", mr: 1 }}
                />
                Mobile Number
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.mobileNumber}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </MotionCard>

      <MotionCard 
        elevation={3} 
        sx={{ mb: 4 }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <CardContent>
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5" component="div">
                <People sx={{ verticalAlign: "middle", mr: 1 }} />
                Attender History
              </Typography>
              {attenders.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <Chip
                      label={`${attenders.length} Attenders`}
                      color="primary"
                      variant="outlined"
                    />
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <Chip
                      icon={<History />}
                      label={`${totalVisits} Visits`}
                      color="info"
                      variant="outlined"
                    />
                  </motion.div>
                </Box>
              )}
            </Box>
          </motion.div>
          
          <Divider sx={{ mb: 3 }} />

          {attenders.length > 0 ? (
            <>
              {attenders.map((attender, index) => (
                <MotionAccordion 
                  key={attender.id}
                  custom={index}
                  variants={accordionVariants}
                  initial="hidden"
                  animate="visible"
                  defaultExpanded={index === 0}
                  sx={{ 
                    mb: 2,
                    borderLeft: `4px solid ${getAttenderTypeColor(attender.attender_type)}`,
                    '&:before': {
                      display: 'none',
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: `${getAttenderTypeColor(attender.attender_type)}10`,
                      '&:hover': {
                        backgroundColor: `${getAttenderTypeColor(attender.attender_type)}20`,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: getAttenderTypeColor(attender.attender_type),
                            mr: 2
                          }}
                        >
                          {getAttenderTypeIcon(attender.attender_type)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {attender.attender_name} 
                            <Chip 
                              size="small" 
                              label={attender.attender_type}
                              sx={{ 
                                ml: 1, 
                                backgroundColor: `${getAttenderTypeColor(attender.attender_type)}20`,
                                color: getAttenderTypeColor(attender.attender_type),
                                fontWeight: 'medium'
                              }}
                            />
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Serial No: {attender.attender_serial_no || "N/A"} • Phone: {attender.phone}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Chip 
                          size="small"
                          label={getAttenderStatusLabel(attender.attender_status)}
                          color={getAttenderStatusColor(attender.attender_status)}
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {attender.attenderHistories && attender.attenderHistories.length > 0 ? (
                      <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0 }}>
                        <Table>
                          <TableHead sx={{ backgroundColor: getAttenderTypeColor(attender.attender_type) }}>
                            <TableRow>
                              <TableCell sx={{ color: "white" }}>Visit Date</TableCell>
                              <TableCell sx={{ color: "white" }}>Check In</TableCell>
                              <TableCell sx={{ color: "white" }}>Check Out</TableCell>
                              <TableCell sx={{ color: "white" }}>Duration</TableCell>
                              <TableCell sx={{ color: "white" }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {attender.attenderHistories.map((history, historyIndex) => {

                              let duration = "N/A";
                              if (history.in_time && history.out_time) {
                                try {
                                  const inTime = new Date(history.in_time.replace(/(\d{2})-(\d{2})-(\d{4}) (.*)/, '$3-$2-$1 $4'));
                                  const outTime = new Date(history.out_time.replace(/(\d{2})-(\d{2})-(\d{4}) (.*)/, '$3-$2-$1 $4'));
                                  const diff = Math.abs(outTime - inTime);
                                  const minutes = Math.floor(diff / 60000);
                                  duration = `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
                                } catch (e) {
                                  console.error("Error calculating duration:", e);
                                  duration = "Error";
                                }
                              }

                          
                              const getDurationColor = () => {
                                if (duration === "N/A") return "rgba(0, 0, 0, 0.02)";
                                if (duration === "Error") return "rgba(244, 67, 54, 0.08)";
                                const hours = parseInt(duration.split('h')[0]);
                                if (hours < 1) return "rgba(76, 175, 80, 0.08)"; 
                                if (hours < 3) return "rgba(255, 235, 59, 0.08)";
                                if (hours < 6) return "rgba(255, 152, 0, 0.08)"; 
                                return "rgba(244, 67, 54, 0.08)"; 
                              };

                              return (
                                <MotionTableRow 
                                  key={history.id}
                                  custom={historyIndex}
                                  variants={tableRowVariants}
                                  initial="hidden"
                                  animate="visible"
                                  sx={{ 
                                    backgroundColor: historyIndex % 2 === 0 ? "white" : getDurationColor(),
                                    '&:hover': { 
                                      backgroundColor: 'rgba(63, 81, 181, 0.08)',
                                      transition: 'background-color 0.3s ease'
                                    } 
                                  }}
                                >
                                  <TableCell>{history.createdAt ? formatDate(history.createdAt) : (history.in_time ? history.in_time.split(' ')[0] : 'N/A')}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Tooltip title="Check In">
                                        <motion.div whileHover={{ scale: 1.2, rotate: 10 }}>
                                          <Avatar 
                                            sx={{ 
                                              width: 24, 
                                              height: 24, 
                                              mr: 1, 
                                              bgcolor: 'green',
                                              fontSize: '0.75rem'
                                            }}
                                          >
                                            IN
                                          </Avatar>
                                        </motion.div>
                                      </Tooltip>
                                      {history.in_time ? history.in_time.split(' ')[1] : 'N/A'}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {history.out_time ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Tooltip title="Check Out">
                                          <motion.div whileHover={{ scale: 1.2, rotate: -10 }}>
                                            <Avatar 
                                              sx={{ 
                                                width: 24, 
                                                height: 24, 
                                                mr: 1, 
                                                bgcolor: 'red',
                                                fontSize: '0.75rem'
                                              }}
                                            >
                                              OUT
                                            </Avatar>
                                          </motion.div>
                                        </Tooltip>
                                        {history.out_time.split(' ')[1]}
                                      </Box>
                                    ) : 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        fontWeight: duration !== "N/A" ? 'medium' : 'normal',
                                        color: duration !== "N/A" ? 
                                          duration.includes('0h') ? 'success.main' :
                                          parseInt(duration) > 5 ? 'error.main' : 'warning.dark'
                                          : 'text.secondary'
                                      }}
                                    >
                                      {duration}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <motion.div whileHover={{ scale: 1.1 }}>
                                      <Chip 
                                        size="small"
                                        label={getAttenderStatusLabel(history.attender_status)}
                                        color={getAttenderStatusColor(history.attender_status)}
                                        sx={{ 
                                          fontWeight: 'medium',
                                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                      />
                                    </motion.div>
                                  </TableCell>
                                </MotionTableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          No visit history found for this attender.
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </MotionAccordion>
              ))}
            </>
          ) : (
            <motion.div 
              variants={itemVariants}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Typography variant="body1" sx={{ textAlign: "center", py: 3 }}>
                No attender history records found for this patient.
              </Typography>
            </motion.div>
          )}
        </CardContent>
      </MotionCard>
    </Box>
  );
};

export default ViewPatient;